import { Middleware } from "redux";
import { IStore } from "redux/store";
import reportActions, {
	EReportActions,
	TReportActions,
	reportJobActions,
} from "./actions";
import { apiActions } from "redux/_core/api";
import reportAPIS from "./api";
import {
	ECNR_BASE_RANGE_TYPE,
	IBilgeWaterAllVesselAggregateRecord,
	IBilgeWaterRecord,
	ICNRRangeColumn,
	ICNRRangeVesselData,
	ICargoSlopAggregateRecord,
	ICargoSlopVesselSpecificAggregateRecord,
	IComprehensiveNoonReportRecord,
	IComprehensiveNoonReportVesselSpecificDataRecord,
	IGarbageCategoryAllVesselsRecord,
	IGarbageDischargeAllVesselsRecord,
	IGarbageCustomSearchRecord,
	IGarbageRawRecord,
	INavnucForms,
	IORSAggregateRecord,
	IORSVesselSpecificAggregateRecord,
	IOilSpillsInWaterAggregateRecord,
	IOilSpillsInWaterVesselSpecificAggregateRecord,
	IPackagingAllVesselAggregatedData,
	IPackagingVesselSpecificAggregatedData,
	ICleaningChemicalsAllVesselAggregatedData,
	ICleaningChemicalsVesselSpecificAggregatedData,
	IEmissionsAllVesselAggregatedData,
	IEmissionsVesselSpecificAggregatedData,
	IRefrigerantAllVesselAggregatedData,
	IRefrigerantVesselSpecificAggregatedData,
	IRefrigerantCustomSearchRecord,
	IBilgeWaterCustomSearchRecord,
	IPackagingCustomSearchRecord,
	ICleaningChemicalsCustomSearchRecord,
	IORSCustomSearchRecord,
	ICargoSlopCustomSearchRecord,
	IEmissionsCustomSearchRecord,
	TBFL1UsageData,
	TBFL2UsageData,
} from "./@types";
import moment from "moment";
import reportSelectors from "./selectors";
import {
	defaultMonthRange,
	quarterLabels as initialQuarterLabels,
	PAGE_SIZE,
	quarterWiseMonthRangeMapper,
} from "./constants";
import { cloneDeep } from "lodash";
import { TEMFUsageData } from "../emf/@types";
import { getMonthStartEnd } from "./utils";
const { apiRequest } = apiActions;

const reportMiddleware: Middleware<any, IStore, any> =
	({ dispatch, getState }) =>
	(next) =>
	(action: TReportActions) => {
		switch (action.type) {
			case EReportActions.REPORT_GARBAGE_LOAD: {
				next(action);
				const job = reportJobActions.garbageReportLoad;
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const reportStore = reportSelectors._getStore(getState());
				const vesselId = reportSelectors.getVesselId(reportStore);
				const yearRange = reportSelectors.getGarbageYear(reportStore);
				const monthRange = defaultMonthRange;

				dispatch(
					apiRequest<IGarbageRawRecord[]>({
						...reportAPIS.loadGarbageReport(
							tenantId,
							vesselId,
							yearRange,
							monthRange,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(reportActions.document.garbageRawDataSet(data));
							},
							onError() {
								dispatch(
									job.error({
										message: "Failed to load garbage report",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_GARBAGE_CUSTOM_SEARCH_LOAD: {
				next(action);
				const job = reportJobActions.garbageCustomSearchLoad;
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const { fromDate, toDate, vesselIds } = action.payload;

				dispatch(
					apiRequest<IGarbageCustomSearchRecord[]>({
						...reportAPIS.loadGarbageCustomSearch(
							tenantId,
							fromDate,
							toDate,
							vesselIds,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.garbageCustomSearchDataSet(data ?? []),
								);
							},
							onError() {
								dispatch(
									job.error({
										message: "Failed to load custom search results",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_BILGE_WATER_CUSTOM_SEARCH_LOAD: {
				next(action);
				const job = reportJobActions.bilgeWaterCustomSearchLoad;
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const { fromDate, toDate, vesselIds } = action.payload;

				dispatch(
					apiRequest<IBilgeWaterCustomSearchRecord[]>({
						...reportAPIS.loadBilgeWaterCustomSearch(
							tenantId,
							fromDate,
							toDate,
							vesselIds,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.bilgeWaterCustomSearchDataSet(
										data ?? [],
									),
								);
							},
							onError() {
								dispatch(
									job.error({
										message: "Failed to load bilge water custom search results",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_ORS_CUSTOM_SEARCH_LOAD: {
				next(action);
				const job = reportJobActions.orsCustomSearchLoad;
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const { fromDate, toDate, vesselIds } = action.payload;

				dispatch(
					apiRequest<IORSCustomSearchRecord[]>({
						...reportAPIS.loadORSCustomSearch(
							tenantId,
							fromDate,
							toDate,
							vesselIds,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.orsCustomSearchDataSet(data ?? []),
								);
							},
							onError() {
								dispatch(
									job.error({
										message: "Failed to load ORS custom search results",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_CARGO_SLOP_CUSTOM_SEARCH_LOAD: {
				next(action);
				const job = reportJobActions.cargoSlopCustomSearchLoad;
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const { fromDate, toDate, vesselIds } = action.payload;

				dispatch(
					apiRequest<ICargoSlopCustomSearchRecord[]>({
						...reportAPIS.loadCargoSlopCustomSearch(
							tenantId,
							fromDate,
							toDate,
							vesselIds,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.cargoSlopCustomSearchDataSet(
										data ?? [],
									),
								);
							},
							onError() {
								dispatch(
									job.error({
										message: "Failed to load Cargo Slop custom search results",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_EMISSIONS_CUSTOM_SEARCH_LOAD: {
				next(action);
				const job = reportJobActions.emissionsCustomSearchLoad;
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const { fromDate, toDate, vesselIds } = action.payload;

				dispatch(
					apiRequest<IEmissionsCustomSearchRecord[]>({
						...reportAPIS.loadEmissionsCustomSearch(
							tenantId,
							fromDate,
							toDate,
							vesselIds,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.emissionsCustomSearchDataSet(
										data ?? [],
									),
								);
							},
							onError() {
								dispatch(
									job.error({
										message: "Failed to load Emissions custom search results",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_BILGE_WATER_LOAD: {
				next(action);
				const job = reportJobActions.garbageReportLoad;
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const reportStore = reportSelectors._getStore(getState());
				const vesselId = reportSelectors.getBilgeWaterVesselId(reportStore);
				const yearRange = reportSelectors.getBilgeWaterYear(reportStore);
				const monthRange = defaultMonthRange;

				dispatch(
					apiRequest<IBilgeWaterRecord[]>({
						...reportAPIS.loadBilgeWaterReport(
							tenantId,
							vesselId,
							yearRange,
							monthRange,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.bilgeWaterRawDataSet(
										data && data?.length ? data : [],
									),
								);
							},
							onError() {
								dispatch(
									job.error({
										message: "Failed to load binge water report",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_BILGE_WATER_ALL_VESSELS_AGGREGATE_LOAD: {
				next(action);
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const reportStore = reportSelectors._getStore(getState());
				const year = reportSelectors.getBilgeWaterYear(reportStore);
				const job = reportJobActions.bilgeWaterAllVesselsAggregateLoad;
				const monthRange = "0,11";

				dispatch(
					apiRequest<IBilgeWaterAllVesselAggregateRecord[]>({
						...reportAPIS.loadBilgeWaterAllVesselsAggregate(
							tenantId,
							year,
							monthRange,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.bilgeWaterAllVesselDataSet(data ?? []),
								);
							},
							onError() {
								dispatch(
									job.error({
										message:
											"Failed to load binge water report for all vessels",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);

				break;
			}
			case EReportActions.REPORT_GARBAGE_DISCHARGE_ALL_VESSELS_LOAD: {
				next(action);
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const reportStore = reportSelectors._getStore(getState());
				const year = reportSelectors.getGarbageYear(reportStore);
				const job = reportJobActions.allVesselsGarbageDischargeLoad;
				const monthRange = "0,11";

				dispatch(
					apiRequest<IGarbageDischargeAllVesselsRecord[]>({
						...reportAPIS.loadGarbageTypeForAllVessels(
							tenantId,
							"dischargeMethod",
							year,
							monthRange,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.garbageDischargeAllVesselDataSet(
										data ?? [],
									),
								);
							},
							onError() {
								dispatch(
									job.error({
										message:
											"Failed to load garbage discharge report for all vessels",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);

				break;
			}
			case EReportActions.REPORT_GARBAGE_CATEGORY_ALL_VESSELS_LOAD: {
				next(action);
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const reportStore = reportSelectors._getStore(getState());
				const year = reportSelectors.getGarbageYear(reportStore);
				const job = reportJobActions.allVesselsGarbageCategoryLoad;
				const monthRange = "0,11";

				dispatch(
					apiRequest<IGarbageCategoryAllVesselsRecord[]>({
						...reportAPIS.loadGarbageTypeForAllVessels(
							tenantId,
							"garbageCategory",
							year,
							monthRange,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.garbageCategoryAllVesselDataSet(
										data ?? [],
									),
								);
							},
							onError() {
								dispatch(
									job.error({
										message:
											"Failed to load garbage category report for all vessels",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);

				break;
			}
			case EReportActions.REPORT_ORS_VESSEL_SPECIFIC_AGGREGATE_LOAD: {
				next(action);
				const job = reportJobActions.vesselSpecificOrsAggregateLoad;
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const reportStore = reportSelectors._getStore(getState());
				const vesselId = reportSelectors.getOrsVesselId(reportStore);

				const year = reportSelectors.getOrsYear(reportStore);
				const monthRange = defaultMonthRange;

				dispatch(
					apiRequest<IORSVesselSpecificAggregateRecord[]>({
						...reportAPIS.loadOrsVesselSpecificAggregate(
							tenantId,
							year,
							monthRange,
							vesselId,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.orsVesselSpecificRawDataSet(
										data && data?.length ? data : [],
									),
								);
							},
							onError() {
								dispatch(
									job.error({
										message: "Failed to load ors report for the vessel",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_ORS_ALL_VESSEL_AGGREGATE_LOAD: {
				next(action);
				const job = reportJobActions.allVesselOrsAggregateLoad;
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const reportStore = reportSelectors._getStore(getState());

				const year = reportSelectors.getOrsYear(reportStore);
				const monthRange = defaultMonthRange;

				dispatch(
					apiRequest<IORSAggregateRecord[]>({
						...reportAPIS.loadOrsAllVesselsAggregate(
							tenantId,
							year,
							monthRange,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.orsRawDataSet(
										data && data?.length ? data : [],
									),
								);
							},
							onError() {
								dispatch(
									job.error({
										message: "Failed to load ors report for the tenant",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_CARGO_SLOP_VESSEL_SPECIFIC_AGGREGATE_LOAD: {
				next(action);
				const job = reportJobActions.vesselSpecificCargoSlopAggregateLoad;
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const reportStore = reportSelectors._getStore(getState());
				const vesselId = reportSelectors.getGenericVesselId(reportStore);

				const year = reportSelectors.getGenericYear(reportStore);
				const monthRange = defaultMonthRange;

				dispatch(
					apiRequest<ICargoSlopVesselSpecificAggregateRecord[]>({
						...reportAPIS.loadCargoSlopVesselSpecificAggregate(
							tenantId,
							year,
							monthRange,
							vesselId,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.cargoSlopVesselSpecificRawDataSet(
										data && data?.length ? data : [],
									),
								);
							},
							onError(err) {
								dispatch(
									job.error({
										message:
											err?.error ??
											"Failed to load cargo slop report for the vessel",
										notification: {},
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_CARGO_SLOP_ALL_VESSEL_AGGREGATE_LOAD: {
				next(action);
				const job = reportJobActions.allVesselCargoSlopAggregateLoad;
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const reportStore = reportSelectors._getStore(getState());

				const year = reportSelectors.getGenericYear(reportStore);
				const monthRange = defaultMonthRange;

				dispatch(
					apiRequest<ICargoSlopAggregateRecord[]>({
						...reportAPIS.loadCargoSlopAllVesselsAggregate(
							tenantId,
							year,
							monthRange,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.cargoSlopRawDataSet(
										data && data?.length ? data : [],
									),
								);
							},
							onError(err) {
								dispatch(
									job.error({
										message:
											err?.error ??
											"Failed to load cargo slop report for the tenant",
										notification: {},
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_OIL_SPILLS_IN_WATER_VESSEL_SPECIFIC_AGGREGATE_LOAD: {
				next(action);
				const job =
					reportJobActions.vesselSpecificOilSpillsInWaterAggregateLoad;
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const reportStore = reportSelectors._getStore(getState());
				const vesselId = reportSelectors.getGenericVesselId(reportStore);

				const year = reportSelectors.getGenericYear(reportStore);
				const monthRange = defaultMonthRange;

				dispatch(
					apiRequest<IOilSpillsInWaterVesselSpecificAggregateRecord[]>({
						...reportAPIS.loadOilSpillsInWaterVesselSpecificAggregate(
							tenantId,
							year,
							monthRange,
							vesselId,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.oilSpillsInWaterVesselSpecificRawDataSet(
										data && data?.length ? data : [],
									),
								);
							},
							onError(err) {
								dispatch(
									job.error({
										message:
											err?.error ??
											"Failed to load oil spill report for the vessel",
										notification: {},
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_OIL_SPILLS_IN_WATER_ALL_VESSEL_AGGREGATE_LOAD: {
				next(action);
				const job = reportJobActions.allVesselOilSpillsInWaterAggregateLoad;
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const reportStore = reportSelectors._getStore(getState());

				const year = reportSelectors.getGenericYear(reportStore);
				const monthRange = defaultMonthRange;

				dispatch(
					apiRequest<IOilSpillsInWaterAggregateRecord[]>({
						...reportAPIS.loadOilSpillsInWaterAllVesselsAggregate(
							tenantId,
							year,
							monthRange,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.oilSpillsInWaterRawDataSet(
										data && data?.length ? data : [],
									),
								);
							},
							onError(err) {
								dispatch(
									job.error({
										message:
											err?.error ??
											"Failed to load oil spill report for the tenant",
										notification: {},
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_COMPREHENSIVE_NOON_REPORT_ALL_VESSEL_LOAD: {
				next(action);
				const job = reportJobActions.allVesselComprehensiveNoonReportLoad;
				// const authStore = getState().app.auth;
				// const reportStore = reportSelectors._getStore(getState());
				// const tenantId = authStore.user?.tenantId ?? "";
				// const vesselType = reportSelectors.getGenericVesselType(reportStore);
				// const vesselClass = reportSelectors.getGenericVesselClass(reportStore);

				dispatch(
					apiRequest<{ data: IComprehensiveNoonReportRecord[] }>({
						...reportAPIS
							.loadComprehensiveNoonReportAllVessels
							// tenantId,
							// vesselType,
							// vesselClass
							(),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.comprehensiveNoonReportRawDataSet(
										data?.data && data?.data?.length ? data.data : [],
									),
								);
							},
							onError(err) {
								dispatch(
									job.error({
										message:
											err?.error ?? "Failed to load comprehensive noon report",
										notification: {},
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_COMPREHENSIVE_NOON_REPORT_VESSEL_SPECIFIC_DATA_LOAD: {
				next(action);
				const job =
					reportJobActions.vesselSpecificComprehensiveNoonReportDataLoad;
				// const authStore = getState().app.auth;
				// const reportStore = reportSelectors._getStore(getState());
				// const vesselId = reportSelectors.getGenericVesselId(reportStore);
				// const tenantId = authStore.user?.tenantId ?? "";

				dispatch(
					apiRequest<IComprehensiveNoonReportVesselSpecificDataRecord[]>({
						...reportAPIS
							.loadComprehensiveNoonReportVesselSpecificData
							// tenantId,
							(),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.comprehensiveNoonReportVesselSpecificDataSet(
										data && data?.length ? data : [],
									),
								);
							},
							onError(err) {
								dispatch(
									job.error({
										message:
											err?.error ?? "Failed to load comprehensive noon report",
										notification: {},
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_CNR_RANGES_VESSELS_LOAD: {
				next(action);
				const reportStore = reportSelectors._getStore(getState());
				const job = reportJobActions.cnrRangesVesselsLoad;
				const { baseRangeType, selectedVessels, paginationParams } =
					action.payload;

				const vesselIds = selectedVessels?.length
					? selectedVessels?.join(",")
					: undefined;

				const columns = reportSelectors.getCNRRangesColumns(reportStore);
				const pagination = reportSelectors.getCNRRangesPagination(reportStore);
				let { limit, skip } = pagination;
				if (paginationParams) {
					limit = paginationParams?.limit;
					skip = paginationParams?.skip;
				}
				dispatch(
					apiRequest<ICNRRangeVesselData>({
						...reportAPIS.loadCNRRangesVesselList({
							limit,
							skip,
							baseRangeType,
							action: "getAllVessels",
							kind: "cnr",
							vesselIds,
						}),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								const vesselsWithRangeData = data?.data;
								if (data?.data && data?.pagination) {
									if (vesselsWithRangeData?.length) {
										dispatch(
											reportActions.document.cnrRangesVesselListSet(
												vesselsWithRangeData,
											),
										);
									} else {
										dispatch(reportActions.document.cnrRangesVesselListSet([]));
									}
								}
								dispatch(
									reportActions.document.cnrRangesPaginationSet({
										hasMore: data?.pagination?.hasMore,
										skip: skip + limit,
									}),
								);
								if (vesselsWithRangeData?.length) {
									const existingColKeys = new Set(
										columns.map((col) => col.key),
									);
									const newColsToAdd: ICNRRangeColumn[] = [];
									vesselsWithRangeData?.forEach((vessel) => {
										if (vessel.localRanges) {
											Object.entries(vessel.localRanges).forEach(
												([metricKey, metricDetail]) => {
													if (
														!existingColKeys.has(metricKey) &&
														!newColsToAdd.some((nc) => nc.key === metricKey)
													) {
														newColsToAdd.push({
															key: metricKey,
															type: metricDetail?.type ?? "",
															title: metricDetail?.metric ?? "",
														});
													}
												},
											);
										}
									});
									dispatch(
										reportActions.document.cnrRangesColumnsSet([
											...columns,
											...newColsToAdd,
										]),
									);
								}
							},
							onError() {
								dispatch(
									job.error({
										message: `Failed to get data for CNR ranges.`,
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_CNR_RANGES_VESSEL_SUB_TYPE_CONFIG_LOAD: {
				next(action);
				const job = reportJobActions.cnrRangesVesselSubTypeConfigLoad;
				const reportStore = reportSelectors._getStore(getState());
				const vesselSubType =
					reportSelectors.getCNRVesselSubType(reportStore) ?? "Container";

				dispatch(
					apiRequest<any>({
						...reportAPIS.loadVesselSubTypeConfig(vesselSubType),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								// TODO data is the config - set it in the store.
							},
							onError(err) {
								// TODO: needs to be removed
								// dispatch(
								// 	reportActions.document.cnrRangesConfigSet(
								// 		signalReportConfig
								// 	)
								// )
								dispatch(
									job.error({
										message: err?.error ?? "Failed to load config",
										notification: {},
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_CNR_RANGES_RANGE_CELL_UPDATE: {
				next(action);

				const { rangeInfo } = action.payload;
				const job = reportJobActions.cnrRangesRangeCellUpdate;
				const reportStore = reportSelectors._getStore(getState());
				const cnrRangeVessels =
					reportSelectors.getCNRRangesRawVesselList(reportStore);
				dispatch(
					apiRequest<any>({
						...reportAPIS.updateCNRRangesRangeCell(rangeInfo), // TODO: It should be rangeInfo within signalLocalRanges
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								// if successful
								dispatch(
									reportActions.document.cnrRangesIsEditModalVisibleToggle(
										false,
									),
								);
								// Optimistic Update - If successfully updated
								// Reason could have called ranges api but it is paginated with infinite-scroll.(Loss of context)
								if (
									data?.message === "Range updated successfully" &&
									data?.response?.ok
								) {
									let updatedCNRRangeVessels = cloneDeep(cnrRangeVessels);
									updatedCNRRangeVessels = updatedCNRRangeVessels?.map(
										(vessel) => {
											const {
												vesselId,
												remarks,
												rangeType,
												newRange,
												metricKey,
											} = rangeInfo;
											if (vessel.vesselId === vesselId) {
												const localRanges = vessel?.localRanges;
												if (localRanges && metricKey in localRanges) {
													localRanges[metricKey] = {
														...localRanges[metricKey],
														range: newRange,
														rangeType,
														remarks,
													};
												}
												return { ...vessel, localRanges };
											} else {
												return { ...vessel };
											}
										},
									);
									dispatch(
										reportActions.document.cnrRangesVesselListSet(
											updatedCNRRangeVessels,
											"update",
										),
									);
									dispatch(
										job.success({
											message: "Successfully updated the range.",
											notification: {},
										}),
									);
								}
							},
							onError(err) {
								console.error(err);
								dispatch(
									job.error({
										message: err?.error ?? "Failed to update Range",
										notification: {},
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);

				break;
			}
			case EReportActions.REPORT_NAVNUC_LOAD: {
				next(action);
				const job = reportJobActions.navnucLoad;
				const reportStore = reportSelectors._getStore(getState());
				const pagination = reportSelectors.getGenericPagination(reportStore);
				dispatch(
					apiRequest<INavnucForms>({
						...reportAPIS.loadNavnuc(pagination.limit, pagination.skip),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.navnucRawDataSet(
										data?.forms && data?.forms?.length ? data.forms : [],
									),
								);
								dispatch(
									reportActions.document.reportPaginationSet(
										data?.pagination
											? data.pagination
											: { hasMore: false, limit: 0, skip: 0, total: 0 },
									),
								);
							},
							onError(err) {
								dispatch(
									job.error({
										message: err?.error ?? "Failed to load Nav/Nuc reports",
										notification: {},
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_CNR_RANGES_CSV_FILE_UPLOAD: {
				next(action);
				const reportStore = reportSelectors._getStore(getState());
				const { file } = action.payload;
				const formData = new FormData();
				const pagination = reportSelectors.getCNRRangesPagination(reportStore);
				formData.append("file", file);
				const job = reportJobActions.cnrRangesCSVFileUpload;
				dispatch(
					apiRequest<any>({
						...reportAPIS.uploadCNRRangesCSVFile(formData), // TODO: It should be rangeInfo within signalLocalRanges
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								console.log({ data });
								// Cleanup
								dispatch(reportActions.document.cnrRangesVesselListReset());
								dispatch(
									reportActions.document.cnrRangesVesselsSelected(undefined),
								);
								dispatch(
									reportActions.document.cnrRangesVesselSubTypeSet(undefined),
								);

								dispatch(
									reportActions.commands.cnrRangesVesselsLoad({
										baseRangeType: ECNR_BASE_RANGE_TYPE.LOCAL,
										selectedVessels: undefined,
										paginationParams: {
											...pagination,
											limit: PAGE_SIZE,
											skip: 0,
											hasMore: true,
										},
									}),
								);
							},
							onError(err) {
								console.error(err);
								dispatch(
									job.error({
										message: err?.error ?? "Failed to upload file.",
										notification: {},
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);

				break;
			}
			// Environmental Reports (EMF) middlewares
			case EReportActions.REPORT_PACKAGING_VESSEL_SPECIFIC_AGGREGATE_DATA_LOAD: {
				next(action);
				const job = reportJobActions.packagingVesselSpecificAggregateDataLoad;
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const { vesselId, year } = action.payload;
				const monthRange = defaultMonthRange;
				dispatch(
					apiRequest<IPackagingVesselSpecificAggregatedData[]>({
						...reportAPIS.loadPackagingDataPerVessel(
							vesselId,
							year,
							monthRange,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.packagingVesselSpecificAggregateDataSet(
										data && data?.length ? data : [],
									),
								);
							},
							onError() {
								dispatch(
									job.error({
										message: "Failed to load packaging report for the vessel",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_PACKAGING_ALL_VESSEL_AGGREGATE_DATA_LOAD: {
				next(action);
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const { year } = action.payload;
				const job = reportJobActions.packagingAllVesselsAggregateDataLoad;
				const monthRange = "0,11";

				dispatch(
					apiRequest<IPackagingAllVesselAggregatedData[]>({
						...reportAPIS.loadPackagingAllVesselAggregatedData(
							tenantId,
							year,
							monthRange,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.packagingAllVesselAggregateDataSet(
										data && data?.length ? data : [],
									),
								);
							},
							onError() {
								dispatch(
									job.error({
										message: "Failed to load packaging report for all vessels",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);

				break;
			}
			case EReportActions.REPORT_CLEANING_CHEMICALS_VESSEL_SPECIFIC_AGGREGATE_DATA_LOAD: {
				next(action);
				const job =
					reportJobActions.cleaningChemicalsVesselSpecificAggregateDataLoad;
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const { vesselId, year } = action.payload;
				const monthRange = defaultMonthRange;
				dispatch(
					apiRequest<ICleaningChemicalsVesselSpecificAggregatedData[]>({
						...reportAPIS.loadCleaningChemicalsDataPerVessel(
							vesselId,
							year,
							monthRange,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.cleaningChemicalsVesselSpecificAggregateDataSet(
										data && data?.length ? data : [],
									),
								);
							},
							onError() {
								dispatch(
									job.error({
										message:
											"Failed to load cleaning chemical report for the vessel",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_CLEANING_CHEMICALS_ALL_VESSEL_AGGREGATE_DATA_LOAD: {
				next(action);
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const { year } = action.payload;
				const job =
					reportJobActions.cleaningChemicalsAllVesselsAggregateDataLoad;
				const monthRange = "0,11";

				dispatch(
					apiRequest<ICleaningChemicalsAllVesselAggregatedData[]>({
						...reportAPIS.loadCleaningChemicalsAllVesselAggregatedData(
							tenantId,
							year,
							monthRange,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.cleaningChemicalsAllVesselAggregateDataSet(
										data && data?.length ? data : [],
									),
								);
							},
							onError() {
								dispatch(
									job.error({
										message:
											"Failed to load cleaning chemicals report for all vessels",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);

				break;
			}
			case EReportActions.REPORT_EMISSIONS_VESSEL_SPECIFIC_AGGREGATE_DATA_LOAD: {
				next(action);
				const job = reportJobActions.emissionsVesselSpecificAggregateDataLoad;
				const { vesselId, year } = action.payload;
				const monthRange = defaultMonthRange;
				dispatch(
					apiRequest<IEmissionsVesselSpecificAggregatedData[]>({
						...reportAPIS.loadEmissionsDataPerVessel(
							vesselId,
							year,
							monthRange,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.emissionsVesselSpecificAggregateDataSet(
										data && data?.length ? data : [],
									),
								);
							},
							onError() {
								dispatch(
									job.error({
										message: "Failed to load emission report for the vessel",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_EMISSIONS_ALL_VESSEL_AGGREGATE_DATA_LOAD: {
				next(action);
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const { year } = action.payload;
				const job = reportJobActions.emissionsAllVesselsAggregateDataLoad;
				const monthRange = "0,11";

				dispatch(
					apiRequest<IEmissionsAllVesselAggregatedData[]>({
						...reportAPIS.loadEmissionsAllVesselAggregatedData(
							tenantId,
							year,
							monthRange,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.emissionsAllVesselAggregateDataSet(
										data && data?.length ? data : [],
									),
								);
							},
							onError() {
								dispatch(
									job.error({
										message: "Failed to load emissions report for all vessels",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);

				break;
			}
			case EReportActions.REPORT_REFRIGERANT_CUSTOM_SEARCH_LOAD: {
				next(action);
				const job = reportJobActions.refrigerantCustomSearchLoad;
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const { fromDate, toDate, vesselIds } = action.payload;

				dispatch(
					apiRequest<IRefrigerantCustomSearchRecord[]>({
						...reportAPIS.loadRefrigerantCustomSearch(
							tenantId,
							fromDate,
							toDate,
							vesselIds,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.refrigerantCustomSearchDataSet(
										data ?? [],
									),
								);
							},
							onError() {
								dispatch(
									job.error({
										message: "Failed to load refrigerant custom search results",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_REFRIGERANT_VESSEL_SPECIFIC_AGGREGATE_DATA_LOAD: {
				next(action);
				const job = reportJobActions.refrigerantVesselSpecificAggregateDataLoad;
				const { vesselId, year } = action.payload;
				const monthRange = defaultMonthRange;
				dispatch(
					apiRequest<IRefrigerantVesselSpecificAggregatedData[]>({
						...reportAPIS.loadRefrigerantDataPerVessel(
							vesselId,
							year,
							monthRange,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.refrigerantVesselSpecificAggregateDataSet(
										data && data?.length ? data : [],
									),
								);
							},
							onError() {
								dispatch(
									job.error({
										message: "Failed to load refrigerant report for the vessel",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_REFRIGERANT_ALL_VESSEL_AGGREGATE_DATA_LOAD: {
				next(action);
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const job = reportJobActions.refrigerantAllVesselsAggregateDataLoad;
				const { year } = action.payload;
				const monthRange = "0,11";

				dispatch(
					apiRequest<IRefrigerantAllVesselAggregatedData[]>({
						...reportAPIS.loadRefrigerantAllVesselAggregatedData(
							tenantId,
							year,
							monthRange,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.refrigerantAllVesselAggregateDataSet(
										data && data?.length ? data : [],
									),
								);
							},
							onError() {
								dispatch(
									job.error({
										message:
											"Failed to load refrigerant report for all vessels",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_FLEET_EMF_USAGE_DATA_LOAD: {
				next(action);
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const job = reportJobActions.emfFleetUsageDataLoad;
				const { yearMonth } = action.payload;
				const [year, month] = yearMonth.split("-");
				const { startDate, endDate } = getMonthStartEnd(+year, +month);
				// Date format example: 2025-12-27T00:00:00+00:00
				let formattedStartDate = `${moment(startDate).format("YYYY-MM-DD")}T00:00:00+00:00`;
				let formattedEndDate = `${moment(endDate).format("YYYY-MM-DD")}T00:00:00+00:00`;
				dispatch(
					apiRequest<TEMFUsageData[]>({
						...reportAPIS.loadEmfFleetUsageReport(
							tenantId,
							formattedStartDate,
							formattedEndDate,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.emfFleetUsageDataSet(
										data && data?.length ? data : [],
									),
								);
							},
							onError() {
								dispatch(
									job.error({
										message: "Failed to load emf usage report for all vessels",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_FLEET_BIOFOULING1_DATA_LOAD: {
				next(action);
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const job = reportJobActions.biofouling1FleetDataLoad;
				const { yearMonth } = action.payload;
				const [year, month] = yearMonth.split("-");
				const { startDate, endDate } = getMonthStartEnd(+year, +month);
				const formattedStartDate = `${moment(startDate).format("YYYY-MM-DD")}T00:00:00+00:00`;
				const formattedEndDate = `${moment(endDate).format("YYYY-MM-DD")}T00:00:00+00:00`;
				dispatch(
					apiRequest<TBFL1UsageData[]>({
						...reportAPIS.loadBiofouling1FleetReport(
							tenantId,
							formattedStartDate,
							formattedEndDate,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.biofouling1FleetDataSet(
										data && data?.length ? data : [],
									),
								);
							},
							onError() {
								dispatch(
									job.error({
										message:
											"Failed to load biofouling-1 report for all vessels",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_FLEET_BIOFOULING2_DATA_LOAD: {
				next(action);
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const job = reportJobActions.biofouling2FleetDataLoad;
				const { yearMonth } = action.payload;
				const [year, month] = yearMonth.split("-");
				const { startDate, endDate } = getMonthStartEnd(+year, +month);
				const formattedStartDate = `${moment(startDate).format("YYYY-MM-DD")}T00:00:00+00:00`;
				const formattedEndDate = `${moment(endDate).format("YYYY-MM-DD")}T00:00:00+00:00`;
				dispatch(
					apiRequest<TBFL2UsageData[]>({
						...reportAPIS.loadBiofouling2FleetReport(
							tenantId,
							formattedStartDate,
							formattedEndDate,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.biofouling2FleetDataSet(
										data && data?.length ? data : [],
									),
								);
							},
							onError() {
								dispatch(
									job.error({
										message:
											"Failed to load biofouling-2 report for all vessels",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_PACKAGING_CUSTOM_SEARCH_LOAD: {
				next(action);
				const job = reportJobActions.packagingCustomSearchLoad;
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const { fromDate, toDate, vesselIds } = action.payload;

				dispatch(
					apiRequest<IPackagingCustomSearchRecord[]>({
						...reportAPIS.loadPackagingCustomSearch(
							tenantId,
							fromDate,
							toDate,
							vesselIds,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.packagingCustomSearchDataSet(
										data ?? [],
									),
								);
							},
							onError() {
								dispatch(
									job.error({
										message: "Failed to load packaging custom search results",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			case EReportActions.REPORT_CLEANING_CHEMICALS_CUSTOM_SEARCH_LOAD: {
				next(action);
				const job = reportJobActions.cleaningChemicalsCustomSearchLoad;
				const authStore = getState().app.auth;
				const tenantId = authStore.user?.tenantId ?? "";
				const { fromDate, toDate, vesselIds } = action.payload;

				dispatch(
					apiRequest<ICleaningChemicalsCustomSearchRecord[]>({
						...reportAPIS.loadCleaningChemicalsCustomSearch(
							tenantId,
							fromDate,
							toDate,
							vesselIds,
						),
						preExecute() {
							dispatch(job.active({}));
						},
						postExecute: {
							onSuccess({ data }) {
								dispatch(
									reportActions.document.cleaningChemicalsCustomSearchDataSet(
										data ?? [],
									),
								);
							},
							onError() {
								dispatch(
									job.error({
										message:
											"Failed to load cleaning chemicals custom search results",
									}),
								);
							},
							finally() {
								dispatch(job.idle({}));
							},
						},
					}),
				);
				break;
			}
			default:
				next(action);
		}
	};

export default reportMiddleware;
